import React, { useCallback, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Submission } from 'services/types/TaskTypes';
import { useEnrollment } from 'services/EnrollmentService';
import { AppContext } from 'context/AppContext';
import CommentDrawer from '../../Comment/CommentDrawer';
import PostItem from 'components/PostItem';

interface FeedItemProps {
  item: Submission;
  onLikeUpdate?: (updatedSubmission: Submission) => void;
  onCommentAdd?: (newComment: any) => void;
  isInCommentDrawer?: boolean;
  showCommentInput?: boolean;
}

const FeedItem: React.FC<FeedItemProps> = ({
  item,
  onLikeUpdate,
  onCommentAdd,
  isInCommentDrawer = false,
  showCommentInput = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [commentDrawerVisible, setCommentDrawerVisible] = useState(false);
  const { authentication, commonData } = useContext(AppContext);
  const { authKey } = authentication;
  const { userDetails } = commonData;

  // Check enrollment status for task navigation
  const { data: enrollmentData } = useEnrollment(
    item.campaign?.id || '',
    userDetails?.id || '',
    authKey || ''
  );

  const handleCampaignClick = useCallback(() => {
    if (item.campaign?.id) {
      navigate(`/campaign?id=${item.campaign.id}`);
    }
  }, [item.campaign?.id, navigate]);

  const handleTaskClick = useCallback(() => {
    if (!item.task?.id || !item.campaign?.id) return;

    // Check if user is enrolled in the campaign
    const isEnrolled = !!enrollmentData;

    if (isEnrolled) {
      // User is enrolled, navigate to task
      navigate(`/task?id=${item.task.id}`);
    } else {
      // User is not enrolled, navigate to campaign
      navigate(`/campaign?id=${item.campaign.id}`);
    }
  }, [item.task?.id, item.campaign?.id, enrollmentData, navigate]);

  const handleCommentClick = useCallback(() => {
    setCommentDrawerVisible(true);
  }, []);

  // Campaign and Task Info component for feed-specific content
  const campaignTaskInfo = item.campaign ? (
    <div className="campaign-task-info">
      <div className="campaign-image-container" onClick={handleCampaignClick}>
        <img 
          src={item.campaign.image_small_url || item.campaign.image_url} 
          alt={item.campaign.name}
          className="campaign-image"
        />
      </div>
      <div className="campaign-task-text">
        <span>{t('#Feed.submission_from_task')} </span>
        <span className="task-name" onClick={handleTaskClick}>
          {item.task?.name}
        </span>
      </div>
    </div>
  ) : null;

  return (
    <>
      <PostItem
        id={item.id}
        message={item.message}
        images={item.images}
        user={item.user}
        created={item.created}
        is_liked={item.is_liked}
        like_count={item.like_count}
        comment_count={item.comment_count}
        onLikeUpdate={onLikeUpdate}
        onCommentClick={handleCommentClick}
        showFollow={true}
        showCommentButton={true}
        beforeContent={campaignTaskInfo}
        className="feed-item-wrapper"
      />

      {/* Comment Drawer */}
      <CommentDrawer
        isOpen={commentDrawerVisible}
        closeHandler={() => setCommentDrawerVisible(false)}
        submission={item}
        onLikeUpdate={onLikeUpdate}
      />
    </>
  );
};

export default FeedItem;